import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { BrowserRouter as Router, Redirect, Route, Switch, useRouteMatch } from 'react-router-dom'
import { AUTH_STATUS, selectIsFirstLogin } from '../../features/auth/authSlice'
import useAuthSession from '../../features/auth/useAuthSession'
import useBiSessionHandoff from '../../features/auth/useBiSessionHandoff'
import { accountApi } from '../../features/account/accountApi'
import { FULL_PAGE_PATHS, LEGACY_PATH, PATH } from '../../app/routes'
import Header from '../SharedComponents/Header/Header'
import SideBar from '../SharedComponents/SideBar/SideBar'
import PageLoader from '../SharedComponents/PageState/PageLoader'
import ConnectorRequired from '../SharedComponents/PageState/ConnectorRequired'
import Dashboard from '../Pages/Dashboard/Dashboard'
import MyProducts from '../Pages/MyProducts/MyProducts'
import ProductDetail from '../Pages/MyProducts/ProductDetail/ProductDetail'
import ViewDetails from '../Modules/ViewDetails/ViewDetails'
import SummaryDetails from '../Modules/ViewDetails/EntityTable/SummaryDetails/SummaryDetails'
import MarketPlace from '../Pages/MarketPlace/MarketPlace'
import CreateConnector from '../Pages/CreateConnector/CreateConnector'
import EGE from '../Pages/EGE/Ege'
import EgeSetupDownload from '../Pages/EGE/EgeSetupDownload/EgeSetupDownload'
import Authentication from '../Pages/Authentication/Authentication'
import Setup from '../Pages/Setup/Setup'
import Configurations from '../Pages/Configurations/Configurations'
import Updates from '../Pages/Updates/Updates'
import Users from '../Pages/Users/Users'
import LandingPage from '../Pages/LandingPage/LandingPage'
import Versions from '../Pages/LandingPage/Versions/Versions'
import Subscription from '../Pages/LandingPage/Subscription/Subscription'

// Full-page workspaces (e.g. Mappings) hide the app header with its notifications and profile menu.
const AppHeader = () => (useRouteMatch(FULL_PAGE_PATHS) ? null : <Header />)

const HomeRedirect = () => {
    const isFirstLogin = useSelector(selectIsFirstLogin)
    return <Redirect to={isFirstLogin ? PATH.UPDATES : PATH.DASHBOARD} />
}
const AuthenticatedApp = () => {
    const dispatch = useDispatch()
    useEffect(() => {
        const request = dispatch(accountApi.endpoints.registerClient.initiate())
        return () => request.reset()
    }, [dispatch])

    return (
        <Router>
            <Switch>
                <Route exact path={PATH.ROOT} component={HomeRedirect} />
                <Route path={PATH.DASHBOARD} component={Dashboard} />
                <Route path={PATH.UPDATES} component={Updates} />
                <Route exact path={PATH.MY_PRODUCTS} component={MyProducts} />
                <Route path={PATH.PRODUCT_DETAIL} component={ProductDetail} />
                <Route path={PATH.VIEW_SUMMARY} component={ViewDetails} />
                <Route path={PATH.SUMMARY_DETAILS} component={SummaryDetails} />
                <Route path={PATH.SETUP} component={Setup} />
                <Route path={PATH.CONFIGURATIONS} component={Configurations} />
                <Route path={PATH.AUTHENTICATION} component={Authentication} />
                <Route
                    exact
                    path={[
                        LEGACY_PATH.VIEW_SUMMARY,
                        LEGACY_PATH.SUMMARY_DETAILS,
                        LEGACY_PATH.SETUP,
                        LEGACY_PATH.CONFIGURATIONS,
                        LEGACY_PATH.AUTHENTICATION
                    ]}
                    component={ConnectorRequired}
                />
                <Route path={PATH.MARKETPLACE} component={MarketPlace} />
                <Route path={PATH.USERS} component={Users} />
                <Route path={PATH.NEW_CONNECTOR} component={CreateConnector} />
                <Route path={PATH.EGE} component={EGE} />
                <Route path={PATH.EGE_SETUP_DOWNLOAD} component={EgeSetupDownload} />
                <Redirect to={PATH.DASHBOARD} />
            </Switch>
            <AppHeader />
            <SideBar />
        </Router>
    )
}

const PublicApp = () => (
    <Router>
        <Switch>
            <Route exact path={PATH.ROOT} component={LandingPage} />
            <Route path={PATH.TRIAL_CONNECTOR} component={Versions} />
            <Route path={PATH.TRIAL_SUBSCRIPTION} component={Subscription} />
            <Redirect to={PATH.ROOT} />
        </Switch>
    </Router>
)

const Routes = () => {
    const status = useAuthSession()
    useBiSessionHandoff()

    if (status === AUTH_STATUS.CHECKING) {
        return <PageLoader fullScreen />
    }
    return status === AUTH_STATUS.AUTHENTICATED ? <AuthenticatedApp /> : <PublicApp />
}

export default Routes
